import type { RescueRequiredDocument } from '../../lib/documentQueries';
import { certificateName, expiringSoon, stateName } from '../../lib/documentLabels';

export interface DocumentActionItem {
  title: string;
  description: string;
  urgency: 'error' | 'warning';
}

/** Date-only, never re-zoned, so it reads as the day written on the certificate. */
function expiryDay(dateOnly: string): string {
  const [year, month, day] = dateOnly.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function subject(entry: RescueRequiredDocument, government: boolean): string {
  if (entry.documentType === 'STATE_EXEMPTION') {
    return `Your ${certificateName(entry.state)}`;
  }
  return government ? 'Your proof of government status' : 'Your 501(c)(3) letter';
}

function consequence(entry: RescueRequiredDocument): string {
  if (entry.documentType === 'STATE_EXEMPTION') {
    return `to keep shipping Fi Nanos to ${entry.state ? stateName(entry.state) : 'that state'}`;
  }
  return 'to keep ordering Fi Minis and Fi Nanos';
}

/** Worded like the account card's uploader, since the letter isn't the only proof accepted. */
function missingProofItem(government: boolean): DocumentActionItem {
  return government
    ? {
        title: 'Upload proof of your government status',
        description: 'Needed on file for Fi Mini and Fi Nano orders',
        urgency: 'warning',
      }
    : {
        title: 'Upload proof of your nonprofit status',
        description: 'Your IRS determination letter or a TEOS printout, needed for Fi Mini and Fi Nano orders',
        urgency: 'warning',
      };
}

/** What the rescue has to do about one document, or null when it needs nothing from them. */
function itemFor(entry: RescueRequiredDocument, government: boolean): DocumentActionItem | null {
  const doc = entry.document;
  if (!doc) {
    // A missing certificate is asked for in the order flow, where the ship-to says which state.
    return entry.documentType === 'NONPROFIT_LETTER' ? missingProofItem(government) : null;
  }
  const renewal = entry.documentType === 'STATE_EXEMPTION' ? 'a renewed certificate' : 'a renewed letter';
  if (doc.status === 'REJECTED') {
    return {
      title: `${subject(entry, government)} wasn't accepted`,
      description: `Upload a replacement ${consequence(entry)}`,
      urgency: 'error',
    };
  }
  if (doc.expired && doc.expirationDate) {
    return {
      title: `${subject(entry, government)} expired ${expiryDay(doc.expirationDate)}`,
      description: `Upload ${renewal} ${consequence(entry)}`,
      urgency: 'error',
    };
  }
  if (expiringSoon(doc) && doc.expirationDate) {
    return {
      title: `${subject(entry, government)} expires ${expiryDay(doc.expirationDate)}`,
      description: `Upload ${renewal} ${consequence(entry)}`,
      urgency: 'warning',
    };
  }
  return null;
}

/** Rejected and expired documents first, then those about to lapse. */
export function documentActionItems(documents: RescueRequiredDocument[], government: boolean): DocumentActionItem[] {
  const items = documents
    .map((entry) => itemFor(entry, government))
    .filter((item): item is DocumentActionItem => item !== null);
  return [...items.filter((item) => item.urgency === 'error'), ...items.filter((item) => item.urgency === 'warning')];
}
